// frontend/src/pages/dashboard/roles/ownerOpsWidgets.tsx
// Shared owner operations query and existing approval widgets.
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/utils/api';
import { useAuthStore } from '@/stores/auth';
import { DeskState } from './ProfileDeskParts';
import { formatINRCompact, formatNum } from './_dashboardShared';

export interface WaitingItem { kind: 'purchase_over_20l' | 'dispatch_held' | 'repeat_escalation'; id: string; title: string; detail: string; value: number | null; action: { label: 'Approve' | 'Override (logged)' | 'Open'; method: 'POST' | 'GET'; path: string; needsReason: boolean } }
export interface OwnerOps {
  dateIst: string; capacityLoaded: boolean;
  headline: { produced: number; producedCapable: number | null; dispatched: number; dispatchCapable: number | null };
  rejectionsToday: number | null; valueAtJobWorkers: { value: number; loaded: boolean } | null;
  belowMin: { count: number; loaded: boolean }; waitingOnYou: WaitingItem[];
}

type OwnerOpsResult = { data: OwnerOps | null; error: string | null };
type Entry = { at: number; promise: Promise<OwnerOpsResult> };
const listeners = new Set<(key: string, result: OwnerOpsResult) => void>();
const cache = new Map<string, Entry>();
export function __resetOwnerOpsCacheForTest() { cache.clear(); }
function validOps(d: OwnerOps | null): d is OwnerOps {
  const number = (v: unknown) => typeof v === 'number' && Number.isFinite(v);
  return !!d && typeof d.dateIst === 'string' && typeof d.capacityLoaded === 'boolean'
    && !!d.headline && [d.headline.produced, d.headline.dispatched].every(number)
    && [d.headline.producedCapable, d.headline.dispatchCapable, d.rejectionsToday].every(v => v === null || number(v))
    && !!d.belowMin && number(d.belowMin.count) && typeof d.belowMin.loaded === 'boolean'
    && (d.valueAtJobWorkers === null || (!!d.valueAtJobWorkers && number(d.valueAtJobWorkers.value) && typeof d.valueAtJobWorkers.loaded === 'boolean'))
    && Array.isArray(d.waitingOnYou) && d.waitingOnYou.every(w => typeof w.id === 'string' && typeof w.title === 'string' && typeof w.detail === 'string'
      && ['dispatch_held', 'purchase_over_20l', 'repeat_escalation'].includes(w.kind) && !!w.action && ['GET','POST'].includes(w.action.method) && typeof w.action.path === 'string');
}
function fetchOwnerOps(key: string, force = false) {
  const previous = cache.get(key);
  if (!force && previous && Date.now() - previous.at < 30_000) return previous.promise;
  const promise = api.get('/analytics/owner-ops').then(response => {
    if (response.data?.success === false) throw Error('Request failed');
    return { data: (response.data?.data ?? response.data) as OwnerOps, error: null };
  }).catch((): OwnerOpsResult => ({ data: null, error: 'Could not load operations.' })).then(result => {
    const checked = result.error || validOps(result.data) ? result : { data: null, error: 'Could not read operations.' };
    if (cache.get(key)?.promise === promise) listeners.forEach(listener => listener(key, checked));
    return checked;
  });
  cache.set(key, { at: Date.now(), promise });
  return promise;
}

export function useOwnerOps() {
  const { user } = useAuthStore();
  const key = `${user?.tenantId}:${user?.id}:${user?.role?.name}`;
  const [state, setState] = useState<{ key: string; data: OwnerOps | null; loading: boolean; error: string | null }>({ key, data: null, loading: true, error: null });
  useEffect(() => {
    let active = true;
    // Discard another identity's cached and in-flight result.
    for (const cachedKey of cache.keys()) if (cachedKey !== key) cache.delete(cachedKey);
    const receive = (receivedKey: string, r: OwnerOpsResult) => {
      if (active && receivedKey === key) setState({ key, ...r, loading: false });
    };
    listeners.add(receive);
    const pending = fetchOwnerOps(key);
    void pending.then(result => { if (cache.get(key)?.promise === pending) receive(key, result); });
    return () => { active = false; listeners.delete(receive); };
  }, [key]);
  const reload = useCallback(() => {
    setState({ key, data: null, loading: true, error: null });
    void fetchOwnerOps(key, true);
  }, [key]);
  return { ...(state.key === key ? state : { data: null, loading: true, error: null }), reload };
}

const Muted = ({ children }: { children: React.ReactNode }) => <div style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 4 }}>{children}</div>;

export function OwnerOpsHeadlineWidget() {
  const resource = useOwnerOps();
  const { data } = resource;
  if (resource.loading || resource.error) return <DeskState resource={resource}/>;
  const pair = (done: number, cap: number | null) => (data?.capacityLoaded && cap ? `${formatNum(done)} / ${formatNum(cap)}` : '—');
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
      <div>
        <div className="kpi-label" style={{ marginBottom: 6 }}>Produced / capable</div>
        <div data-testid="ops-produced" className="num-display" style={{ fontSize: 32 }}>{data ? pair(data.headline.produced, data.headline.producedCapable) : '—'}</div>
      </div>
      <div>
        <div className="kpi-label" style={{ marginBottom: 6 }}>Dispatched / capable</div>
        <div data-testid="ops-dispatched" className="num-display" style={{ fontSize: 32 }}>{data ? pair(data.headline.dispatched, data.headline.dispatchCapable) : '—'}</div>
      </div>
      {data && !data.capacityLoaded && <div style={{ gridColumn: '1 / -1' }}><Muted>Capacity not loaded — these numbers start once capacity is entered.</Muted></div>}
    </div>
  );
}

export function RejectionsTodayWidget() {
  const resource = useOwnerOps();
  const { data } = resource;
  if (resource.loading || resource.error) return <DeskState resource={resource}/>;
  return (<><div className="kpi-label" style={{ marginBottom: 6 }}>Rejections today</div>
    <div data-testid="ops-rejections" className="num-display" style={{ fontSize: 28, color: data?.rejectionsToday ? 'var(--danger)' : undefined }}>{data?.rejectionsToday == null ? '—' : formatNum(data.rejectionsToday)}</div></>);
}

export function JobWorkerValueWidget() {
  const resource = useOwnerOps();
  const { data } = resource;
  if (resource.loading || resource.error) return <DeskState resource={resource}/>;
  return (<><div className="kpi-label" style={{ marginBottom: 6 }}>At job-workers</div>
    <div data-testid="ops-jobwork" className="num-display" style={{ fontSize: 28 }}>{data?.valueAtJobWorkers?.loaded ? formatINRCompact(data.valueAtJobWorkers.value) : <span className="chip" style={{ fontSize: 11 }}>Not loaded</span>}</div></>);
}

export function BelowMinWidget() {
  const resource = useOwnerOps();
  const { data } = resource;
  if (resource.loading || resource.error) return <DeskState resource={resource}/>;
  return (<><div className="kpi-label" style={{ marginBottom: 6 }}>Below min</div>
    {data?.belowMin.loaded
      ? <div data-testid="ops-below-min" className="num-display" style={{ fontSize: 28 }}>{formatNum(data.belowMin.count)}</div>
      : <><div data-testid="ops-below-min" className="num-display" style={{ fontSize: 28 }}>—</div><Muted>Min levels not loaded</Muted></>}</>);
}

export function WaitingOnYouWidget() {
  const resource = useOwnerOps();
  const { data, reload } = resource;
  const navigate = useNavigate();
  const [busy, setBusy] = useState<string | null>(null);
  const [message, setMessage] = useState<{ code?: string; text: string } | null>(null);

  const act = async (w: WaitingItem) => {
    setMessage(null);
    if (w.action.method === 'GET') { navigate(w.action.path); return; }
    let body: Record<string, string> = {};
    if (w.action.needsReason) {
      const reason = window.prompt('Why are you overriding this hold? It is logged under your name.');
      if (!reason || reason.trim().length < 5) return;
      body = { reason: reason.trim() };
    }
    setBusy(w.id);
    try { await api.post(w.action.path, body); reload(); }
    catch (err: any) { setMessage({ code: err?.response?.data?.code, text: err?.response?.data?.error || 'That action did not go through.' }); }
    finally { setBusy(null); }
  };

  if (resource.loading || resource.error) return <DeskState resource={resource}/>;
  return (
    <>
      <div className="kpi-label" style={{ marginBottom: 10 }}>Waiting on you</div>
      {message && <div role="alert" data-error-code={message.code} style={{ fontSize: 12, color: 'var(--danger)', marginBottom: 8 }}>{message.text}</div>}
      {!data || data.waitingOnYou.length === 0 ? <Muted>Nothing waiting on you.</Muted> : (
        <table style={{ width: '100%', fontSize: 13, borderCollapse: 'collapse' }}>
          <thead><tr style={{ color: 'var(--ink-3)', textAlign: 'left' }}><th style={{ padding: '4px 0' }}>Waiting on you</th><th>Detail</th><th style={{ textAlign: 'right' }}>Value</th><th /></tr></thead>
          <tbody>
            {data.waitingOnYou.map((w) => (
              <tr key={`${w.kind}:${w.id}`} style={{ borderTop: '1px solid var(--line)' }}>
                <td style={{ padding: '8px 0', color: 'var(--ink-0)' }}>{w.title}</td>
                <td style={{ color: 'var(--ink-2)' }}>{w.detail}</td>
                <td style={{ textAlign: 'right' }}>{w.value == null ? '—' : formatINRCompact(w.value)}</td>
                <td style={{ textAlign: 'right' }}>
                  <button className="btn btn-outline" style={{ fontSize: 12, padding: '4px 10px' }} disabled={busy === w.id} onClick={() => act(w)}>{w.action.label}</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </>
  );
}
